import React from "react";
import {
  Autocomplete,
  TextField,
  InputAdornment,
  Select,
  MenuItem
} from "@mui/material";
import { ReactComponent as SearchIcon } from "@src/assets/icons/search.svg";
import { ReactComponent as CloseIcon } from "@src/assets/icons/close-black.svg";
import usePlacesAutocomplete, {
  getGeocode,
  getLatLng
} from "use-places-autocomplete";
import { ISearchLocationOption, SearchLocationProps } from "./types";
import { EMPTY_VALUE } from "@src/core/config";
import { selectWeatherExpanded, setMapLocation } from "@src/core/controllers";
import { useDispatch, useSelector } from "react-redux";
import { ArcOutlinedInput } from "@src/core/ui";
import { toast } from "react-toastify";
import { useCameraLayout } from "@src/features/order/components/camera-view/camera-layout-context";

type SearchMode = "address" | "coordinates";

const WRAPPER_STYLE: React.CSSProperties = {
  position: "absolute",
  top: "-2px",
  left: "-5%",
  right: 0,
  zIndex: 1000,
  pointerEvents: "none"
};

const CONTAINER_STYLE: React.CSSProperties = {
  backgroundColor: "#fff",
  maxWidth: 420,
  margin: "10px auto",
  padding: "8px",
  minHeight: 16,
  maxHeight: 45,
  display: "flex",
  alignItems: "center",
  gap: "8px",
  borderRadius: "8px",
  boxShadow: "0px 4px 8px 0px #0000004D"
};

const ROW_STYLE: React.CSSProperties = {
  backgroundColor: "#fff",
  display: "flex",
  alignItems: "center",
  gap: "0px",
  flex: 1,
  width: "100%",
  minWidth: 0
};

const COORDS_ROW_STYLE: React.CSSProperties = {
  ...ROW_STYLE,
  justifyContent: "space-between"
};

const SEARCH_MODE_MENU_PROPS = {
  PaperProps: {
    sx: {
      marginTop: 2,
      backgroundColor: "white",
      "& .MuiMenuItem-root": {
        color: "black",
        fontSize: 12,
        padding: "4px 10px"
      }
    }
  }
};

const SEARCH_MODE_SELECT_SX = {
  height: 28,
  padding: "0px",
  backgroundColor: "background.paper",
  color: "white",
  fontSize: 12,
  "& .MuiOutlinedInput-notchedOutline": { border: "none" },
  "& svg": { color: "white" }
};

const COORD_INPUT_SX = {
  width: 128,
  "& .MuiOutlinedInput-root": {
    marginTop: "4px",
    height: 28,
    fontSize: 12,
    backgroundColor: "#fff",
    color: "#000",
    "& fieldset": { borderColor: "#1C1C1C" },
    "&:hover fieldset": { borderColor: "#1C1C1C" },
    "&.Mui-focused fieldset": { borderColor: "#1C1C1C" },
    "& input": { color: "#000", padding: "7px" }
  }
};

const ADDRESS_INPUT_SX = {
  "& .MuiOutlinedInput-root": {
    backgroundColor: "#fff",
    color: "#000",
    padding: 0,
    marginLeft: "-8px",
    "& fieldset": { borderColor: "transparent" },
    "&:hover fieldset": { borderColor: "transparent" },
    "&.Mui-focused fieldset": { borderColor: "transparent" },
    "& input": { color: "#000", padding: 0 }
  }
};


export const SearchLocation: React.FC<SearchLocationProps> = (props) => {
  const { initValue, addPosition } = props;
  const { viewMode } = useCameraLayout();
  const weatherExpanded = useSelector(selectWeatherExpanded);

  const containerStyle: React.CSSProperties = React.useMemo(
    () => ({
      ...CONTAINER_STYLE,
      margin: weatherExpanded
        ? `10px 230px`
        : "10px auto",
    }),
    [weatherExpanded]
  );

  const {
    suggestions: { status, data },
    setValue,
    value
  } = usePlacesAutocomplete({
    defaultValue: initValue ?? EMPTY_VALUE
  });

  const dispatch = useDispatch();

  const [searchMode, setSearchMode] = React.useState<SearchMode>("address");
  const [latitude, setLatitude] = React.useState("");
  const [longitude, setLongitude] = React.useState("");

  const onSelect = React.useCallback(
    (lat: number, lng: number, address: string) => {
      dispatch(
        setMapLocation({
          latitude: lat,
          longitude: lng,
          address
        })
      );
      addPosition({ lat, lng });
    },
    [dispatch, addPosition]
  );

  const fetchGeocode = async (address: string) => {
    try {
      const results = await getGeocode({ address });
      const { lat, lng } = await getLatLng(results[0]);
      onSelect(lat, lng, address);
    } catch (error) {
      console.error(error);
    }
  };

  const fetchAddressByCoords = async (lat: number, lng: number) => {
    try {
      const geocoder = new google.maps.Geocoder();
      const response = await geocoder.geocode({
        location: { lat, lng }
      });

      if (response.results[0]) {
        const address = response.results[0].formatted_address;
        onSelect(lat, lng, address);
        setValue(address);
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleCoordsSubmit = async () => {
    const lat = parseFloat(latitude);
    const lng = parseFloat(longitude);

    if (isNaN(lat) || isNaN(lng)) {
      toast.warning("Please enter valid coordinates");
      return;
    }

    await fetchAddressByCoords(lat, lng);
  };

  const options: ISearchLocationOption[] =
    status === "OK"
      ? data.map((s) => ({
        label: s.description,
        value: s.description
      }))
      : [];

  const handleSearchModeChange = (e: { target: { value: unknown } }) =>
    setSearchMode(e.target.value as SearchMode);

  const searchModeSelect = (
    <Select
      value={searchMode}
      onChange={handleSearchModeChange}
      size="small"
      sx={{
        ...SEARCH_MODE_SELECT_SX,
        minWidth: 80
      }}
      MenuProps={SEARCH_MODE_MENU_PROPS}
    >
      <MenuItem value="address">Address</MenuItem>
      <MenuItem value="coordinates">Coordinates</MenuItem>
    </Select>
  );

  const coordsModeSelect = (
    <Select
      value={searchMode}
      onChange={handleSearchModeChange}
      size="small"
      sx={{
        ...SEARCH_MODE_SELECT_SX,
        maxWidth: 115
      }}
      MenuProps={SEARCH_MODE_MENU_PROPS}
    >
      <MenuItem value="address">Address</MenuItem>
      <MenuItem value="coordinates">Coordinates</MenuItem>
    </Select>
  );

  if (viewMode !== 'default') return null;

  return (
    <div style={WRAPPER_STYLE}>
      <div style={{ pointerEvents: "auto" }}>
        <div style={containerStyle}>
          {searchMode === "address" ? (
            <div style={ROW_STYLE}>
              <Autocomplete
                freeSolo
                disableClearable
                sx={{ flex: 1, minWidth: 0 }}
                options={options}
                value={value}
                onInputChange={(_, v) => setValue(v)}
                onChange={(_, option) =>
                  option &&
                  fetchGeocode(
                    typeof option === "string" ? option : option.value
                  )
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    placeholder="Search for location"
                    size="small"
                    fullWidth
                    sx={ADDRESS_INPUT_SX}
                    InputProps={{
                      ...params.InputProps,
                      endAdornment: (
                        <InputAdornment position="end">
                          {value && (
                            <CloseIcon
                              onClick={() => setValue("")}
                              style={{ cursor: "pointer", marginRight: 4 }}
                            />
                          )}
                          <SearchIcon
                            onClick={() => value && fetchGeocode(value)}
                            style={{ cursor: "pointer", marginLeft: '4px' }}
                          />
                        </InputAdornment>
                      )
                    }}
                  />
                )}
              />
              {searchModeSelect}
            </div>
          ) : (
            <div style={COORDS_ROW_STYLE}>
              <ArcOutlinedInput
                size="small"
                placeholder="Latitude"
                value={latitude}
                onChange={(e) => setLatitude(e.target.value)}
                sx={{
                  ...COORD_INPUT_SX,
                  "& .MuiOutlinedInput-root": {
                    ...COORD_INPUT_SX["& .MuiOutlinedInput-root"],
                    marginLeft: "1px"
                  } 
                }}
              />
              <ArcOutlinedInput
                size="small"
                placeholder="Longitude"
                value={longitude}
                onChange={(e) => setLongitude(e.target.value)}
                sx={{
                  ...COORD_INPUT_SX
                }}
              />
              <SearchIcon
                onClick={handleCoordsSubmit}
                style={{ cursor: "pointer" }}
              />
              {coordsModeSelect}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
